// src/screens/FaqAdminScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { 
  FaEdit, 
  FaTrash, 
  FaArrowLeft, 
  FaHome, 
  FaSignOutAlt, 
  FaQuestionCircle, 
  FaPlus, 
  FaSave, 
  FaTimes 
} from 'react-icons/fa';

const APP_PRIMARY = '#b03060';

const FaqAdminScreen = () => {
  const [faqs, setFaqs] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [respuesta, setRespuesta] = useState('');
  const [editando, setEditando] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [faqAEliminar, setFaqAEliminar] = useState(null);
  const [cargando, setCargando] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const cargarFaqs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'faq'));
        const datos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFaqs(datos);
      } catch (error) {
        console.error('Error al cargar FAQs:', error);
      } finally {
        setCargando(false);
      }
    };
    cargarFaqs();
  }, []);

  const guardarFaq = async () => {
    if (!pregunta.trim() || !respuesta.trim()) return;

    try {
      if (editando) {
        await updateDoc(doc(db, 'faq', editando), { pregunta, respuesta });
        setFaqs(prev => prev.map(f => f.id === editando ? { ...f, pregunta, respuesta } : f));
      } else {
        const docRef = await addDoc(collection(db, 'faq'), { pregunta, respuesta });
        setFaqs([{ id: docRef.id, pregunta, respuesta }, ...faqs]);
      }

      cancelarEdicion();
    } catch (error) {
      console.error('Error al guardar FAQ:', error);
    }
  };

  const editar = (id, p, r) => {
    setEditando(id);
    setPregunta(p);
    setRespuesta(r);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelarEdicion = () => {
    setPregunta('');
    setRespuesta('');
    setEditando(null);
  };

  const confirmarEliminar = (id) => {
    setFaqAEliminar(id);
    setShowModal(true);
  };

  const eliminar = async () => {
    if (!faqAEliminar) return;
    try {
      await deleteDoc(doc(db, 'faq', faqAEliminar));
      setFaqs(prev => prev.filter(f => f.id !== faqAEliminar));
    } catch (error) {
      console.error('Error al eliminar FAQ:', error);
    } finally {
      setShowModal(false);
      setFaqAEliminar(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error al cerrar sesión: ' + err.message);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Cabecera superior */}
        <div style={styles.headerRow}>
          <button onClick={() => navigate('/admin')} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver al panel
          </button>
          <span style={styles.headerBadge}>Centro de Ayuda</span>
        </div>

        <div style={styles.titleSection}>
          <h2 style={styles.title}>Preguntas Frecuentes (FAQ)</h2>
          <p style={styles.subtitle}>
            Crea, edita o elimina las respuestas a dudas comunes que ven tus alumnos
          </p>
        </div>

        {/* Tarjeta de Formulario */}
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <div style={styles.iconBox}>
              <FaQuestionCircle size={16} color={APP_PRIMARY} />
            </div>
            <h3 style={styles.formTitle}>
              {editando ? 'Editar Pregunta Frecuente' : 'Nueva Pregunta Frecuente'}
            </h3>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Pregunta</label>
            <input
              type="text"
              placeholder="Ej: ¿Qué pasa si no logro realizar la rutina completa?"
              value={pregunta}
              onChange={e => setPregunta(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Respuesta detallada</label>
            <textarea
              rows={4}
              placeholder="Escribe la explicación clara y recomendaciones para el alumno..."
              value={respuesta}
              onChange={e => setRespuesta(e.target.value)}
              style={styles.textarea}
            />
          </div>

          <div style={styles.formActions}>
            {editando && (
              <button onClick={cancelarEdicion} style={styles.cancelFormBtn}>
                <FaTimes size={12} /> Cancelar
              </button>
            )}
            <button onClick={guardarFaq} style={styles.saveButton}>
              {editando ? (
                <>
                  <FaSave size={13} /> Actualizar Pregunta
                </>
              ) : (
                <>
                  <FaPlus size={12} /> Agregar Pregunta
                </>
              )}
            </button>
          </div>
        </div>

        {/* Listado de Preguntas */}
        <div style={styles.listSection}>
          <div style={styles.listHeaderRow}>
            <h3 style={styles.listSectionTitle}>Preguntas Registradas</h3>
            <span style={styles.counterBadge}>
              {faqs.length} {faqs.length === 1 ? 'pregunta' : 'preguntas'}
            </span>
          </div>

          {cargando ? (
            <div style={styles.emptyCard}>
              <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
                Cargando preguntas frecuentes...
              </p>
            </div>
          ) : faqs.length === 0 ? (
            <div style={styles.emptyCard}>
              <FaQuestionCircle size={36} color="#cbd5e1" style={{ marginBottom: 10 }} />
              <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
                No hay preguntas frecuentes registradas aún.
              </p>
            </div>
          ) : (
            <div style={styles.faqList}>
              {faqs.map((faq, idx) => (
                <div key={faq.id} style={styles.faqCard}>
                  <div style={styles.faqTopRow}>
                    <div style={styles.faqQuestionWrapper}>
                      <span style={styles.indexBadge}>#{idx + 1}</span>
                      <strong style={styles.faqQuestionText}>{faq.pregunta}</strong>
                    </div>

                    <div style={styles.faqActionButtons}>
                      <button
                        onClick={() => editar(faq.id, faq.pregunta, faq.respuesta)}
                        style={styles.editButton}
                        title="Editar pregunta"
                      >
                        <FaEdit size={13} />
                      </button>
                      <button
                        onClick={() => confirmarEliminar(faq.id)}
                        style={styles.deleteButton}
                        title="Eliminar pregunta"
                      >
                        <FaTrash size={13} />
                      </button>
                    </div>
                  </div>

                  <p style={styles.faqAnswerText}>{faq.respuesta}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Confirmación de Eliminación */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>¿Eliminar pregunta frecuente?</h3>
            <p style={styles.modalText}>
              Esta acción no se puede deshacer y la pregunta ya no estará visible para los usuarios en la app.
            </p>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowModal(false)} style={styles.modalCancelBtn}>
                Cancelar
              </button>
              <button onClick={eliminar} style={styles.modalConfirmBtn}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dock inferior flotante */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={navDockStyles.dockButton}
          >
            <div style={navDockStyles.iconWrapper}>
              <FaHome size={14} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{ ...navDockStyles.dockButton, color: '#dc2626' }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={13} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaqAdminScreen;

/* =========================== ESTILOS =========================== */
const styles = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px 120px 16px',
    boxSizing: 'border-box',
    fontFamily: "'Poppins', sans-serif"
  },
  container: {
    maxWidth: 860,
    margin: '0 auto'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  backButton: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '8px 16px',
    borderRadius: 50,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease'
  },
  headerBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  titleSection: {
    marginBottom: 24
  },
  title: {
    color: '#1e293b',
    fontSize: 26,
    fontWeight: '700',
    margin: '0 0 6px 0',
    letterSpacing: '-0.3px'
  },
  subtitle: {
    color: '#64748b',
    fontSize: 14,
    margin: 0
  },
  formCard: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '24px 28px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
    marginBottom: 32
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: '1px solid #f1f5f9'
  },
  iconBox: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  formGroup: {
    marginBottom: 16
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: '600',
    color: '#475569',
    marginBottom: 6
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1e293b',
    transition: 'border-color 0.2s ease'
  },
  textarea: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1e293b',
    resize: 'vertical',
    minHeight: 90,
    transition: 'border-color 0.2s ease'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20
  },
  cancelFormBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 18px',
    borderRadius: 50,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: APP_PRIMARY,
    color: '#ffffff',
    padding: '10px 22px',
    border: 'none',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    boxShadow: '0 3px 10px rgba(176, 48, 96, 0.25)',
    transition: 'all 0.2s ease'
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  listHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4
  },
  listSectionTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  counterBadge: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748b',
    background: '#ffffff',
    padding: '3px 10px',
    borderRadius: 20,
    border: '1px solid #e2e8f0'
  },
  emptyCard: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '40px 20px',
    textAlign: 'center',
    border: '1px dashed #cbd5e1'
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  faqCard: {
    background: '#ffffff',
    borderRadius: 18,
    padding: '18px 22px',
    border: '1.5px solid #f1f5f9',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.2s ease'
  },
  faqTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 10
  },
  faqQuestionWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    flex: 1
  },
  indexBadge: {
    fontSize: 11,
    fontWeight: '700',
    background: '#fdf2f8',
    color: APP_PRIMARY,
    padding: '2px 8px',
    borderRadius: 6,
    border: '1px solid #fbcfe8',
    marginTop: 2,
    flexShrink: 0
  },
  faqQuestionText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e293b',
    lineHeight: 1.4
  },
  faqActionButtons: {
    display: 'flex',
    gap: 6,
    flexShrink: 0
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  deleteButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  faqAnswerText: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 1.6,
    margin: 0,
    paddingLeft: 34
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 3000,
    padding: 16,
    boxSizing: 'border-box'
  },
  modalBox: {
    background: '#ffffff',
    padding: 24,
    borderRadius: 20,
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    border: '1px solid #f1f5f9'
  },
  modalTitle: {
    color: '#dc2626',
    marginTop: 0,
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 8
  },
  modalText: {
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.5,
    margin: '0 0 20px 0'
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10
  },
  modalCancelBtn: {
    padding: '9px 16px',
    borderRadius: 50,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer'
  },
  modalConfirmBtn: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: '#dc2626',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(220, 38, 38, 0.25)'
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};